import React from "react"
import { Helmet } from "react-helmet-async"
import { Link } from "react-router-dom"

const FAQJsonLd = ({ faqs }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    }}
  />
)

const LocalBusinessJsonLd = ({ title, description, areaServed }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        name: "Devbhumi Educational Dreamzone",
        url: "https://devbhumielearning.in/",
        description,
        telephone: "+91-8328830471",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Bhadrak",
          addressRegion: "Odisha",
          postalCode: "756100",
          addressCountry: "IN",
        },
        areaServed,
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: title,
          itemListElement: [
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Admission counselling" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Form fill-up & document support" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Online coaching/classes" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Study material / book distribution" } },
          ],
        },
      }),
    }}
  />
)

export default function SeoLandingPage({
  title,
  description,
  path,
  h1,
  intro,
  bullets,
  faqs,
  areaServed,
  keywords,
}) {
  const url = `https://devbhumielearning.in${path}`
  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden max-w-[100vw]">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords ? <meta name="keywords" content={keywords} /> : null}
        <link rel="canonical" href={url} />
        <meta name="robots" content="index,follow,max-image-preview:large" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />

        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <LocalBusinessJsonLd
        title={title}
        description={description}
        areaServed={areaServed}
      />
      {faqs?.length ? <FAQJsonLd faqs={faqs} /> : null}

      <main className="container mx-auto px-4 sm:px-6 py-10 sm:py-16 max-w-4xl min-w-0">
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6 min-w-0">
          <Link
            to="/"
            className="text-sm font-bold text-blue-700 hover:underline text-center sm:text-left shrink-0"
          >
            ← Back to Home
          </Link>
          <a
            href="tel:+918328830471"
            className="text-sm font-bold bg-blue-700 text-white px-4 py-3 sm:py-2 rounded-lg hover:bg-blue-800 text-center whitespace-nowrap shrink-0 w-full sm:w-auto"
          >
            Call for Admission
          </a>
        </div>

        <h1 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight mt-8 sm:mt-10 break-words">
          {h1}
        </h1>
        <p className="text-gray-600 mt-4 sm:mt-6 text-base sm:text-lg leading-relaxed break-words">
          {intro}
        </p>

        <div className="mt-8 sm:mt-10 bg-gray-50 border border-gray-100 rounded-2xl p-4 sm:p-6 min-w-0">
          <h2 className="text-lg sm:text-xl font-black break-words">What we provide</h2>
          <ul className="mt-4 space-y-2 text-gray-700">
            {bullets.map((b) => (
              <li key={b} className="flex gap-2">
                <span className="mt-2 w-2 h-2 rounded-full bg-blue-700 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {faqs?.length ? (
          <section className="mt-12">
            <h2 className="text-xl sm:text-2xl font-black break-words">FAQs</h2>
            <div className="mt-6 space-y-4">
              {faqs.map((f) => (
                <details
                  key={f.q}
                  className="border border-gray-200 rounded-xl p-5 bg-white"
                >
                  <summary className="font-black cursor-pointer break-words pr-2">
                    {f.q}
                  </summary>
                  <p className="text-gray-600 mt-3 leading-relaxed break-words">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </section>
        ) : null}

        <section className="mt-12 border-t border-gray-100 pt-10">
          <h2 className="text-xl sm:text-2xl font-black break-words">Office & Support</h2>
          <p className="text-gray-600 mt-4 leading-relaxed break-words text-sm sm:text-base">
            Office: Jagannathpur, near Bonth Chowk, opposite of Satsang Vihar
            Mandir, Bhadrak, Odisha 756100. We help Odisha students with
            counselling, admission processing, book distribution and online
            classes.
          </p>
        </section>
      </main>
    </div>
  )
}

